import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { ErrorPage } from "@/components/Error/ErrorPage";
import type { DateRangeOption } from "@/modules/analytics/util";
import { useAnalyticsQueries } from "@/queries/analytics/queryOptions";
import { useTranslation } from "@/translations";
import type { NonEmptyArray } from "@/types/utility-types";

import { EngagementAnalyticsDetails, type EngagementAnalyticsDetailsProps } from "./EngagementAnalyticsDetails";

export function EngagementDetailPage(): React.ReactNode {
  const { t } = useTranslation();

  const dateRangeOptions = useMemo<NonEmptyArray<DateRangeOption>>(
    () => [
      {
        name: t("common.date-range.last-months", { count: 6 }),
        value: "last6Months",
      },
      {
        name: t("common.date-range.last-year"),
        value: "lastYear",
      },
    ],
    [t],
  );

  const [dateRange, setDateRange] = useState<DateRangeOption>(dateRangeOptions[0]);

  const queries = useAnalyticsQueries();
  const {
    data: engagementDetails,
    isPending: isPendingEngagementDetails,
    error: engagementDetailsError,
  } = useQuery(queries.engagementsDetails(dateRange.value));
  const {
    data: engagementsData,
    isPending: isPendingEngagementsData,
    error: engagementsDataError,
  } = useQuery(queries.engagement());

  const error = engagementDetailsError || engagementsDataError;
  if (error) {
    return <ErrorPage error={error} />;
  }

  const contentProps: EngagementAnalyticsDetailsProps = {
    engagementDetails,
    engagementStats: engagementsData?.engagements || [],
    isLoading: isPendingEngagementDetails || isPendingEngagementsData,
    selectedDateRange: dateRange,
    dateRangeOptions,
    onDateRangeChange: setDateRange,
  };

  return <EngagementAnalyticsDetails {...contentProps} />;
}
